import appConfig from '../../configs/appConfig.js';
import envVariables from '../../consts/envVariables';
import * as uamfMethods from '../../consts/uamf/uamfMethods';
import {selectIotDeviceAssetId} from '../../state/selectors/iotDevices';
import {getState} from '../../state/store';
import damMappingService from '../dam/damMappingService';
import uamfClient from './uamfClient';
import uamfMappingService from './uamfMappingService';

const postAuthToken = async (serialNumber, assetId, consents) => {
    const consentFlags = Object.keys(consents).filter((key) => consents[key]);

    const body = {
        externalId: envVariables.APP_TYPE,
        userType: envVariables.APP_TYPE,
        appType: envVariables.APP_TYPE,
        market: appConfig.getMarket(),
        ageVerified: true,
        assets: [
            {
                id: serialNumber,
                idType: 'SERIAL',
            },
        ],
        consentFlags,
    };

    const queryParams = {
        project: envVariables.UAMF_PROJECT,
        assetId, // Unique per-device query param to avoid reusing cached response for same URL
    };

    return await uamfClient.callPost({
        endpoint: envVariables.INC_SHARED_API_ENDPOINT,
        path: uamfMethods.AUTH_TOKEN,
        body,
        mapping: damMappingService.authTokenMapping,
        isThrowError: true,
        isOfflineResponse: true,
        queryParams,
    });
};

const getRequestStatus = (serialNumber, requestId) =>
    uamfClient.callGet({
        path: uamfMethods.REQUEST_STATUS,
        mapping: uamfMappingService.requestStatusMapping,
        serialNumber,
        isUamToken: true,
        pathParams: [requestId],
    });

const getFirmwareUpdateAvailability = (serialNumber, language, isCached) => {
    const queryParams = {
        language,
        releaseNotes: true,
    };

    return uamfClient.callGet({
        path: uamfMethods.FIRMWARE_AVAILABILITY,
        serialNumber,
        isCached,
        isThrowError: true,
        isUamToken: true,
        isOfflineResponse: true,
        mapping: damMappingService.firmwareAvailabilityMapping,
        queryParams,
    });
};

const getSubscription = (serialNumber) => {
    return uamfClient.callGet({
        path: uamfMethods.SUBSCRIPTION,
        mapping: uamfMappingService.subscriptionMapping,
        serialNumber,
        isUamToken: true,
        isThrowError: true,
    });
};

const getDeviceInfo = (serialNumber, isThrowError, isCached) => {
    const assetId = selectIotDeviceAssetId(serialNumber)(getState());

    return uamfClient.callGet({
        path: uamfMethods.ASSETS,
        mapping: uamfMappingService.deviceInfoMapping,
        serialNumber,
        isCached,
        isThrowError,
        isUamToken: true,
        isOfflineResponse: true,
        queryParams: {
            'asset-id': assetId,
        },
    });
};

const getDeviceConnectionStatus = (serialNumber, isThrowError) =>
    uamfClient.callGet({
        path: uamfMethods.CONNECTIVITY_STATUS,
        mapping: uamfMappingService.deviceConnectionStatusMapping,
        serialNumber,
        isUamToken: true,
        isThrowError,
    });

const postDeviceSynchronize = (serialNumber) => {
    return uamfClient.callPost({
        path: uamfMethods.SYNCHRONIZE,
        mapping: uamfMappingService.deviceSynchronisationMapping,
        serialNumber,
        isUamToken: true,
        isThrowError: true,
    });
};

const postUpdateSetting = (serialNumber, settingName, newValue) => {
    const queryParams = {
        settingName,
    };

    const body = {
        settingName,
        newValue,
        appRequestTimestamp: String(Date.now()),
    };

    return uamfClient.callPost({
        path: uamfMethods.UPDATE_SETTING,
        serialNumber,
        isThrowError: true,
        isUamToken: true,
        isOnlineRequest: true,
        body,
        queryParams,
        mapping: uamfMappingService.updateSettingMapping,
    });
};

const getRegistrationStatus = (serialNumber) =>
    uamfClient.callGet({
        path: uamfMethods.REGISTRATION_STATUS,
        serialNumber,
        isUamToken: true,
        isThrowError: true,
    });

const postUserDevice = async ({dusn, codentify, deviceName}) => {
    const body = {};

    if (dusn) {
        body.dusn = dusn;
    }

    if (codentify) {
        body.codentify = codentify;
    }

    if (deviceName) {
        body.device_name = deviceName;
    }

    return await uamfClient.callPost({
        path: uamfMethods.USER_DEVICES,
        mapping: uamfMappingService.registrationMapping,
        body,
        isThrowError: true,
    });
};

const postGenerateExperienceInference = (serialNumber) =>
    uamfClient.callPost({
        path: uamfMethods.EXPERIENCE_INFERENCE_GENERATE,
        serialNumber,
        isUamToken: true,
        isThrowError: true,
    });

const getExperienceInference = (serialNumber) =>
    uamfClient.callGet({
        path: uamfMethods.EXPERIENCE_INFERENCE_GET,
        serialNumber,
        isThrowError: true,
        isUamToken: true,
        isOfflineResponse: true,
        mapping: uamfMappingService.experienceInferenceMapping,
    });

const delUserDevice = async (assetId) => {
    const queryParams = {
        asset_id: assetId,
    };

    await uamfClient.callDel({
        path: uamfMethods.USER_DEVICES,
        queryParams,
        isThrowError: true,
    });
};

export default {
    postAuthToken,
    getRequestStatus,
    getFirmwareUpdateAvailability,
    getSubscription,
    getDeviceInfo,
    getDeviceConnectionStatus,
    postDeviceSynchronize,
    postUpdateSetting,
    getRegistrationStatus,
    postUserDevice,
    delUserDevice,
    postGenerateExperienceInference,
    getExperienceInference,
};
